import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import tinycolor from 'tinycolor2';
import LeadCaptureForm from './LeadCaptureForm';
import { buildPageThemeStyle, getThemeColor, PAGE_THEME_DEFAULTS } from '@/components/CampaignPages/utils/pageTheme';
import { getEnv } from '@/lib/env';
import HeroSection from '@/components/CampaignPages/shared/HeroSection';
import StorySection from '@/components/CampaignPages/shared/StorySection';
import ContactSection from '@/components/CampaignPages/shared/ContactSection';
import CtaFooterSection from '@/components/CampaignPages/shared/CtaFooterSection';
import sharedStyles from '@/components/CampaignPages/shared/sections.module.scss';
import styles from './PublicPageTemplate.module.scss';

const resolveBrandColor = (color, fallback) =>
  !color || ['#ffffff', '#fff'].includes(color.toLowerCase()) ? fallback : color;

const splitHeadline = text => {
  if (!text?.trim()) return { main: '', accent: '' };
  const [main, ...rest] = text.split('\n');
  return { main: main.trim(), accent: rest.join(' ').trim() };
};

const renderBodyText = text => {
  if (!text?.trim()) return null;
  return text
    .split('\n')
    .filter(line => line.trim())
    .map((line, i) => <p key={i}>{line}</p>);
};

const formatAddress = ({ address, address_1, address_2, city, state, zip_code } = {}) => {
  const line1 = address || [address_1, address_2].filter(Boolean).join(', ');
  return [line1, city, state, zip_code].filter(Boolean).join(', ');
};

const resolveContactInfo = (page, currentUser) => {
  const pageUser = page?.user;
  if (pageUser) {
    const { email, phone, city, state, zip_code, address } = pageUser;
    return { email, phone, address: formatAddress({ address, city, state, zip_code }) };
  }

  const profile = currentUser?.profile;
  return {
    email: currentUser?.email,
    phone: profile?.phone,
    address: formatAddress(profile)
  };
};

const PublicPageTemplate = ({ page, preview, mobileLayout, currentUser, slug }) => {
  const pageSlug = slug ?? page.slug;
  const corporation = page.corporation || {};
  const logoUrl = corporation.logo_url || corporation.logo?.url;
  const headlineAlignment = page.headline_alignment || PAGE_THEME_DEFAULTS.headline_alignment;

  const colors = useMemo(() => {
    const primary =
      getThemeColor(page, 'header_color') || resolveBrandColor(corporation.color_1, PAGE_THEME_DEFAULTS.header_color);
    const heroEnd = getThemeColor(page, 'accent_color') || PAGE_THEME_DEFAULTS.accent_color;
    const accent =
      getThemeColor(page, 'cta_color') || resolveBrandColor(corporation.color_2, PAGE_THEME_DEFAULTS.cta_color);
    const third =
      getThemeColor(page, 'body_text_color') ||
      resolveBrandColor(corporation.color_3, PAGE_THEME_DEFAULTS.body_text_color);
    const heroText = getThemeColor(page, 'hero_text_color') || PAGE_THEME_DEFAULTS.hero_text_color;

    return {
      primary,
      heroEnd,
      accent,
      third,
      heroText,
      heroAccent: tinycolor(heroText).isDark() ? tinycolor(heroText).lighten(35).toString() : accent
    };
  }, [corporation, page]);

  const navBackground = useMemo(() => tinycolor(colors.primary).darken(5).toString(), [colors.primary]);

  const heroHeadline = splitHeadline(page.headline);
  const storyBody = renderBodyText(page.body_text);
  const showStory = !!(
    page.secondary_headline ||
    storyBody ||
    (page.video_type && page.video_type !== 'none' && page.video_url)
  );
  const showFooter = !!(page.headline || page.supporting_copy);
  const contact = useMemo(() => resolveContactInfo(page, currentUser), [page, currentUser]);

  useEffect(() => {
    if (preview) return;

    const startTime = Date.now();
    let sent = false;

    const sendTimeSpent = () => {
      if (sent) return;
      sent = true;
      const seconds = Math.round((Date.now() - startTime) / 1000);
      if (seconds < 1) return;
      const url = `${getEnv('NEXT_PUBLIC_API_URL') || ''}/v1/public/campaign_pages/${pageSlug}/track_session`;
      const blob = new Blob([JSON.stringify({ time_spent_seconds: seconds })], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    };

    const onHidden = () => document.visibilityState === 'hidden' && sendTimeSpent();

    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('beforeunload', sendTimeSpent);

    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('beforeunload', sendTimeSpent);
      sendTimeSpent();
    };
  }, [pageSlug, preview]);

  const scrollToForm = useCallback(() => {
    document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const scrollToContact = useCallback(() => {
    document.getElementById('get-in-touch')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const video = useMemo(
    () => ({ type: page.video_type, url: page.video_url, thumbnailUrl: page.video_thumbnail_url }),
    [page.video_type, page.video_url, page.video_thumbnail_url]
  );

  return (
    <div className={classNames(styles.page, mobileLayout && styles.pageMobile)} style={buildPageThemeStyle(page)}>
      <HeroSection
        background={colors.primary}
        navBackground={navBackground}
        colors={colors}
        logoUrl={logoUrl}
        corporationName={corporation.name}
        headline={heroHeadline}
        subtitle={page.supporting_copy}
        headlineAlignment={headlineAlignment}
        onViewOpportunities={scrollToForm}
        onContact={scrollToContact}
        mobileLayout={mobileLayout}
      />

      {showStory && (
        <StorySection
          colors={colors}
          heading={page.secondary_headline}
          body={storyBody}
          video={video}
          preview={preview}
          mobileLayout={mobileLayout}
        />
      )}

      <section id="get-started" className={styles.formSection}>
        <div className={sharedStyles.container}>
          <div className={styles.formHeader}>
            <p className={sharedStyles.sectionLabel} style={{ color: colors.accent }}>
              GET STARTED
            </p>
            <h2 className={sharedStyles.sectionHeading} style={{ color: colors.third }}>
              Take the First Step
            </h2>
            <div className={styles.formDivider} style={{ background: colors.accent }} />
          </div>
          <LeadCaptureForm page={page} slug={pageSlug} preview={preview} currentUser={currentUser} flat />
        </div>
      </section>

      <ContactSection colors={colors} contact={contact} mobileLayout={mobileLayout} />

      {showFooter && (
        <CtaFooterSection
          background={colors.primary}
          colors={colors}
          showSubtitle={!!page.supporting_copy}
          onViewOpportunities={scrollToForm}
          onContact={scrollToContact}
          mobileLayout={mobileLayout}
        />
      )}
    </div>
  );
};

export default PublicPageTemplate;
